import { forwardRef } from 'react';
import { AvatarProps, AvatarType, AvatarVariant, AvatarSize } from './Avatar.types';
import {
  avatarVariants,
  avatarContainerClasses,
  avatarIconClasses,
  iconSizeClasses,
  statusContainerClasses,
  statusOffsetClasses,
} from './Avatar.style';
import { Status } from '../status';
import { StatusType, StatusState } from '../status/Status.types';
import { darkenToContrast } from '@/lib/colorUtils';
import { cn } from '@/lib/styleUtils';

/**
 * Palette of background colours for name-based avatars.
 *
 * ## Invariants:
 * - These are BASE hues, darkened at point of use rather than stored ready-to-render. Why: all
 *   four were paired with white and all four failed WCAG 1.4.3 — teal worst at 1.98:1 — and
 *   since the colour is picked by hashing the parker's name, whether their initials were
 *   legible came down to how their name happened to hash. Darkening in HSL keeps the designed
 *   hue instead of substituting a different colour (PB-5610).
 */
/**
 * The teal account badge shown in the headers. Stored as the DESIGNED hue and darkened at
 * render, rather than as a ready-to-use value: white initials on the raw teal measure 1.98:1,
 * the worst contrast found in the accessibility audit (PB-5610).
 */
export const AVATAR_ACCENT = '#28CCC9';

const AVATAR_BACKGROUNDS: string[] = [
  '#28CCC9', // teal
  '#CCAB28', // gold
  '#D973B2', // pink
  '#6ABC75', // green
];

/** Simple string hash → consistent palette index */
const getColorFromName = (name: string): { bg: string; text: string } => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const base = AVATAR_BACKGROUNDS[Math.abs(hash) % AVATAR_BACKGROUNDS.length];
  // Same hue, lightness lowered only as far as white initials need.
  return { bg: darkenToContrast(base), text: '#FFFFFF' };
};

/**
 * Extracts initials from a full name.
 * - "John Payne" → "JP"
 * - "John" → "J"
 * - "John Middle Payne" → "JP" (first + last)
 */
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      type = AvatarType.initials,
      variant = AvatarVariant.primary,
      size = AvatarSize.medium,
      isOutline = false,
      icon,
      name,
      initials,
      showStatus = false,
      statusState = StatusState.positive,
      className,
    },
    ref
  ) => {
    const displayInitials = initials || (name ? getInitials(name) : '') || 'U';

    const renderContent = () => {
      if (type === AvatarType.icon && icon) {
        return (
          <span
            className={cn(avatarIconClasses, iconSizeClasses[size], 'tw:text-inherit')}
            aria-hidden="true"
          >
            {icon}
          </span>
        );
      }
      return (
        <span className="tw:text-inherit tw:[font-size:inherit]">{displayInitials}</span>
      );
    };

    const nameColor =
      variant === AvatarVariant.colorFromName && name ? getColorFromName(name) : null;

    // Same rule as the hashed palette: keep the designed hue, lower its lightness only as far
    // as white initials require.
    const accentColor =
      variant === AvatarVariant.accent
        ? { bg: darkenToContrast(AVATAR_ACCENT), text: '#FFFFFF' }
        : null;

    const derivedColor = nameColor ?? accentColor;

    const avatarElement = (
      <div
        ref={!showStatus ? ref : undefined}
        className={cn(avatarVariants({ variant, size, isOutline }), className)}
        // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (avatar colours derived from the name hash / accent hue)
        style={
          derivedColor ? { backgroundColor: derivedColor.bg, color: derivedColor.text } : undefined
        }
        role="img"
        aria-label={name || 'Avatar'}
        data-cy="avatar"
      >
        {renderContent()}
      </div>
    );

    // Wrap with container if showing status
    if (showStatus) {
      return (
        <div ref={ref} className={avatarContainerClasses}>
          {avatarElement}
          <div className={cn(statusContainerClasses, statusOffsetClasses)}>
            <Status type={StatusType.icon} status={statusState} />
          </div>
        </div>
      );
    }

    return avatarElement;
  }
);

Avatar.displayName = 'Avatar';
